/**
 * useCheckStepFooter — the shared advance discipline for a keyed check step
 * (KYCID-FSD-00013 §8), generalised from Aadhaar's OPTIONAL(skip) /
 * REQUIRED(suppress-except-unavailable→fallback) footer.
 *
 * A check step advances the flow through the store's `checkGuards`:
 *  - `satisfy()`  — the check genuinely passed (verified). Marks satisfied.
 *  - `fallback()` — the honest fallback: a REQUIRED PRE_CALL check whose provider
 *    is unavailable, or a live-OVD handled with the agent on the call. Marks
 *    fallback (which clears a REQUIRED check — the customer is never dead-ended
 *    for a provider wobble; the agent verifies live).
 *  - `skip()`     — OPTIONAL only: continue without it.
 *
 * REQUIRED steps SUPPRESS the plain skip affordance (only `fallback` continues,
 * and only when the provider is genuinely unavailable). OPTIONAL steps never block.
 */

import { useCallback, useMemo } from 'react';
import type { CheckKey, CheckRequirement } from '@/types';
import { useSessionStore } from '@/store/sessionStore';
import { isMockMode } from '@/api/client';
import { reportPlanReadFailure } from '../planTelemetry';

export interface CheckStepFooter {
  /** The clamped requirement for this check (from the frozen plan). */
  readonly requirement: CheckRequirement;
  readonly isRequired: boolean;
  readonly isOptional: boolean;
  /** Advance recording the check as genuinely satisfied (verified). */
  readonly satisfy: () => void;
  /** Advance via the honest fallback (provider unavailable / live-OVD on the call). */
  readonly fallback: () => void;
  /** OPTIONAL-only skip. On a REQUIRED step this is a no-op (the affordance is suppressed). */
  readonly skip: () => void;
}

export function useCheckStepFooter(checkKey: CheckKey): CheckStepFooter {
  const plan = useSessionStore((s) => s.plan);
  const setCheckGuard = useSessionStore((s) => s.setCheckGuard);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);

  const sessionId = useSessionStore((s) => s.session.sessionId);
  // 18-Aug RCA (owner item #2): FAIL-CLOSED. The old default was `?? 'OPTIONAL'` — an unknown
  // requirement rendered a skip, which is how a REQUIRED DigiLocker (whose plan row was never read)
  // became a silent client-side bypass the be's queue gate then refused in a loop. Outside mock
  // mode an unknown requirement is now REQUIRED (skip suppressed) and the miss is reported. Mock
  // mode keeps OPTIONAL so the demo walkthrough advances.
  const requirement: CheckRequirement = useMemo(() => {
    const row = plan.find((p) => p.checkKey === checkKey);
    if (row) return row.requirement;
    if (isMockMode()) return 'OPTIONAL';
    reportPlanReadFailure(sessionId ?? 'unknown', 'empty');
    return 'REQUIRED';
  }, [plan, checkKey, sessionId]);

  const satisfy = useCallback(() => {
    setCheckGuard(checkKey, 'satisfied');
    advance();
  }, [checkKey, setCheckGuard, advance]);

  const fallback = useCallback(() => {
    setCheckGuard(checkKey, 'fallback');
    advance();
  }, [checkKey, setCheckGuard, advance]);

  const skip = useCallback(() => {
    if (requirement === 'REQUIRED') return; // suppressed on REQUIRED
    setCheckGuard(checkKey, 'skipped');
    advance();
  }, [checkKey, requirement, setCheckGuard, advance]);

  return {
    requirement,
    isRequired: requirement === 'REQUIRED',
    isOptional: requirement === 'OPTIONAL',
    satisfy,
    fallback,
    skip,
  };
}
